import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
  TextField,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { AcessoComponentes } from '../../../../../../shared/services/api/c4-model/SistemasService';

type Row = {
  id: string;
  nome: string;
  isEditing?: boolean;
  isNew?: boolean;
};

type Props = {
  initialRows?: AcessoComponentes[];
  onAcessoComponentesChange?: (acessos: AcessoComponentes[]) => void;
};

const convertToRows = (acessoComponentes: AcessoComponentes[]): Row[] => {
  return acessoComponentes.map((item) => ({
    id: item.id,
    nome: item.nome,
  }));
};

export const ListaAcessoComponentes: React.FC<Props> = ({
  initialRows = [],
  onAcessoComponentesChange,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editingValues, setEditingValues] = useState<{ nome: string }>({
    nome: '',
  });

  useEffect(() => {
    if (initialRows.length > 0) {
      setRows(convertToRows(initialRows));
    } else {
      setRows([]);
    }
  }, [initialRows]);

  const handleEdit = (row: Row) => {
    setEditingRowId(row.id);
    setEditingValues({
      nome: row.nome,
    });
  };

  const handleSave = (id: string) => {
    const newRows = rows.map((row) =>
      row.id === id
        ? {
          ...row,
          nome: editingValues.nome,
          isEditing: false,
          isNew: false,
        }
        : row
    );
    setRows(newRows);
    setEditingRowId(null);
    setEditingValues({ nome: '' });

    if (onAcessoComponentesChange) {
      const acessoComponentes = newRows
        .filter((row) => !row.isNew)
        .map((row) => ({
          id: row.id,
          nome: row.nome,
        }));
      onAcessoComponentesChange(acessoComponentes);
    }
  };

  const handleCancel = (id: string) => {
    const newRows = rows.filter((row) => !(row.id === id && row.isNew));
    setRows(newRows);
    setEditingRowId(null);
    setEditingValues({ nome: '' });
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);
    if (onAcessoComponentesChange) {
      const acessoComponentes = newRows.map((row) => ({
        id: row.id,
        nome: row.nome,
      }));
      onAcessoComponentesChange(acessoComponentes);
    }
  };

  const handleAddNew = () => {
    const newRow: Row = {
      id: String(Date.now()),
      nome: '',
      isEditing: true,
      isNew: true,
    };
    setRows([newRow, ...rows]);
    setEditingRowId(newRow.id);
    setEditingValues({ nome: '' });
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      <Button
        variant="contained"
        color="primary"
        sx={{ width: '80px' }}
        onClick={handleAddNew}
        startIcon={<AddIcon />}
      >
        Novo
      </Button>
      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Componente</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>
                  {editingRowId === row.id ? (
                    <>
                      <Tooltip title="Salvar">
                        <IconButton
                          size="small"
                          onClick={() => handleSave(row.id)}
                          disabled={!editingValues.nome.trim()}
                          color="primary"
                        >
                          <SaveIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancelar">
                        <IconButton
                          size="small"
                          onClick={() => handleCancel(row.id)}
                        >
                          <CancelIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(row.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => handleEdit(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </TableCell>
                <TableCell>
                  {editingRowId === row.id ? (
                    <TextField
                      value={editingValues.nome}
                      onChange={(e) =>
                        setEditingValues({ nome: e.target.value })
                      }
                      placeholder="Nome do componente"
                      size="small"
                      fullWidth
                      autoFocus
                    />
                  ) : (
                    row.nome
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
